import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import {
  FOLLOW_DIRECTIONS_PROGRESS_CUE,
  FOLLOW_DIRECTIONS_REPLAYED_CUE,
  FOLLOW_DIRECTIONS_UNDONE_CUE,
  getFollowDirectionsFeedbackCue
} from "@/audio/curriculum-catalog/focus-social-feedback-cues";
import {
  calculateInstructionScore,
  createInstructionSessionState,
  getNextInstructionStep,
  instructionPayloadSchema,
  reduceInstructionSession,
  type InstructionHint,
  type InstructionPayload,
  type InstructionSessionAction,
  type InstructionSessionState
} from "@/game/templates/instruction";

export type FocusSocialFollowDirectionsAction = InstructionSessionAction;

export interface FocusSocialDirectionTargetModel {
  readonly id: string;
  readonly label: string;
  readonly isAvailable: boolean;
  readonly isCompleted: boolean;
  readonly isHinted: boolean;
}

export interface FocusSocialDirectionFeedbackModel {
  readonly audioPath: string;
  readonly kind: "hint" | "progress" | "retry";
  readonly message: string;
  readonly playbackRequest: number;
}

export interface FocusSocialFollowDirectionsRenderModel {
  readonly status: InstructionSessionState["status"];
  readonly completedStepCount: number;
  readonly currentStepAudioPath: string | null;
  readonly currentStepCaption: string | null;
  readonly currentStepNumber: number;
  readonly currentStepPlaybackRequest: number | null;
  readonly totalStepCount: number;
  readonly targets: readonly FocusSocialDirectionTargetModel[];
  readonly feedback: FocusSocialDirectionFeedbackModel | null;
}

function getActiveHint(
  state: InstructionSessionState
): InstructionHint | null {
  const transition = state.lastTransition;

  if (transition?.kind === "hint" || transition?.kind === "retry") {
    return transition.hint;
  }

  return null;
}

function toFeedbackModel(
  state: InstructionSessionState
): FocusSocialDirectionFeedbackModel | null {
  const transition = state.lastTransition;

  if (transition === null) {
    return null;
  }

  const playbackRequest =
    state.acceptedActionCount +
    state.retryCount +
    state.undoCount +
    state.replayCount +
    state.hintState.hintCount;

  switch (transition.kind) {
    case "accepted":
      return transition.completed
        ? null
        : {
            audioPath: FOLLOW_DIRECTIONS_PROGRESS_CUE.audioPath,
            kind: "progress",
            message: FOLLOW_DIRECTIONS_PROGRESS_CUE.text,
            playbackRequest
          };
    case "retry": {
      const cue = getFollowDirectionsFeedbackCue(
        transition.hint.stage,
        "retry"
      );

      return {
        audioPath: cue.audioPath,
        kind: "retry",
        message: cue.text,
        playbackRequest
      };
    }
    case "hint": {
      const cue = getFollowDirectionsFeedbackCue(
        transition.hint.stage,
        "hint"
      );

      return {
        audioPath: cue.audioPath,
        kind: "hint",
        message: cue.text,
        playbackRequest
      };
    }
    case "undone":
      return {
        audioPath: FOLLOW_DIRECTIONS_UNDONE_CUE.audioPath,
        kind: "progress",
        message: FOLLOW_DIRECTIONS_UNDONE_CUE.text,
        playbackRequest
      };
    case "instruction-replayed":
      return {
        audioPath: FOLLOW_DIRECTIONS_REPLAYED_CUE.audioPath,
        kind: "hint",
        message: FOLLOW_DIRECTIONS_REPLAYED_CUE.text,
        playbackRequest
      };
  }
}

function getAllowedTargetIds(
  hint: InstructionHint | null
): ReadonlySet<string> | null {
  if (hint?.stage !== "reduced-choices") {
    return null;
  }

  return new Set(hint.targetIds);
}

function toRenderModel(
  state: InstructionSessionState,
  payload: InstructionPayload
): FocusSocialFollowDirectionsRenderModel {
  const nextStep = getNextInstructionStep(payload, state);
  const activeHint = getActiveHint(state);
  const allowedTargetIds = getAllowedTargetIds(activeHint);
  const completedTargetIds = new Set(
    state.responses.map(({ targetId }) => targetId)
  );

  return {
    status: state.status,
    completedStepCount: state.responses.length,
    currentStepAudioPath: nextStep?.audio ?? null,
    currentStepCaption: nextStep?.caption ?? null,
    currentStepNumber: Math.min(
      state.responses.length + 1,
      payload.steps.length
    ),
    currentStepPlaybackRequest:
      nextStep === undefined ? null : state.responses.length,
    totalStepCount: payload.steps.length,
    targets: payload.targets.map((target) => ({
      id: target.id,
      label: target.label,
      isAvailable:
        !completedTargetIds.has(target.id) &&
        (allowedTargetIds === null || allowedTargetIds.has(target.id)),
      isCompleted: completedTargetIds.has(target.id),
      isHinted:
        activeHint?.stage === "replay-demonstration" &&
        nextStep?.targetId === target.id
    })),
    feedback: toFeedbackModel(state)
  };
}

function getMetrics(
  state: InstructionSessionState,
  payload: InstructionPayload
): ActivityCompletionMetrics {
  const score = calculateInstructionScore(payload, state);

  return {
    completed: score.completed,
    score: score.score,
    correctRate: score.correctRate,
    hintCount: score.hintCount,
    retryCount: score.retryCount
  };
}

export const focusSocialFollowDirectionsAdapter: ActivityAdapter<
  InstructionPayload,
  InstructionSessionState,
  FocusSocialFollowDirectionsAction,
  FocusSocialFollowDirectionsRenderModel
> = {
  activityType: "follow-directions",
  parsePayload: (value) => instructionPayloadSchema.parse(value),
  createInitialState: (payload, context) => {
    void payload;
    void context;
    return createInstructionSessionState();
  },
  reduce: (state, payload, action, context) => {
    void context;
    return reduceInstructionSession(state, payload, action);
  },
  getMetrics,
  toRenderModel
};
